import type { SearchResponse } from '~/types/search';

import { get } from '@helsenorge/core-cms/search-v2/page/utils';
import { useQuery, type LocalQueryResult } from '@helsenorge/core-utils/useQuery';

type PaginatedSearchResponse = SearchResponse & {
  // Søkeordet responsen ble hentet for – brukes til å forkaste data fra forrige søk
  fetchedForQuery: string;
};

interface UsePaginatedSearchOptions {
  // Relativ URL til søkesiden (samme som SearchPageProps.searchUrl)
  searchUrl: string;
  // Aktivt søkeord – improvedQuery || query
  query: string;
  // Innholdsreferanse (samme som SearchPageProps.contentReference)
  contentReference: string | null;
  // Om fallback-/pagineringssøk skal kjøre – typisk useFallback && !!query
  enabled: boolean;
}

type PaginatedQueryResult = LocalQueryResult<PaginatedSearchResponse | null, [string, number]>;

interface UsePaginatedSearchResult extends Pick<PaginatedQueryResult, 'isLoading' | 'isError' | 'fetchNext'> {
  pagination: PaginatedSearchResponse | null;
}

export function usePaginatedSearch({ searchUrl, query, contentReference, enabled }: UsePaginatedSearchOptions): UsePaginatedSearchResult {
  const { data, isLoading, isError, fetchNext } = useQuery<PaginatedSearchResponse | null, [string, number]>({
    queryKey: ['pagination', query],
    args: [query, 0],
    queryFn: async (signal, q, s) => {
      const response = await get<SearchResponse | null>(
        `${searchUrl}search`,
        { q, skip: s, contentReference: contentReference ?? undefined },
        signal
      );
      return response ? { ...response, fetchedForQuery: q } : response;
    },
    merge: (old, next) =>
      old && next && old.fetchedForQuery === next.fetchedForQuery ? { ...next, hits: [...(old.hits ?? []), ...(next.hits ?? [])] } : next,
    staleTime: 30_000,
    keepPreviousData: true,
    enabled,
  });

  const pagination = data?.fetchedForQuery === query ? data : null;
  return { pagination, isLoading, isError, fetchNext };
}
